import { canFlyTo, inRectArea, isFlyDestination, isRectArea, type WorldMapArea, type WorldMapView } from '../../shared/world-map.ts';
import { confirmDialog } from '../common/confirm-dialog.ts';
import { $, el } from '../common/dom.ts';
import { Modal } from '../common/modal.ts';

/**
 * Finesse du calque des lieux (par pixel de la carte du monde), et arrondi des coins des villes (en pixels de la carte).
 * Sur une grande image, le calque est moins fin pour ne pas dépasser OVERLAY_MAX_SIDE (un canevas a une taille maximale).
 */
const OVERLAY_SCALE = 8;
const OVERLAY_MAX_SIDE = 4096;
const TOWN_CORNER = 0.6;

/** Niveaux de zoom des boutons +/-, et celui de l'ouverture (centré sur le joueur). */
const ZOOM_LEVELS = [1, 1.5, 2, 3, 4, 6];
const OPENING_ZOOM = 2;
/** Déplacement (en pixels d'écran) au-delà duquel un appui devient un glissement de la carte, et non un clic. */
const DRAG_THRESHOLD = 4;

const forEachPixel = (area: WorldMapArea, visit: (x: number, y: number) => void): void => {
  if (area.kind === 'route') for (const [x, y] of area.pixels) visit(x, y);
  else for (let y = area.y; y < area.y + area.height; y++) for (let x = area.x; x < area.x + area.width; x++) visit(x, y);
};

/** Laisse passer les images en cours avant un calcul long (préchargement en arrière-plan). */
const idle = (): Promise<void> => new Promise((resolve) => {
  if ('requestIdleCallback' in window) requestIdleCallback(() => resolve(), { timeout: 2000 });
  else setTimeout(resolve, 200);
});

const inArea = (area: WorldMapArea, x: number, y: number): boolean => isRectArea(area)
  ? inRectArea(area, x, y)
  : area.pixels.some(([px, py]) => px === x && py === y);

/**
 * Carte du monde (touche M) : le dessin de l'administration, la zone où l'on se trouve et le joueur. Avec le Vol, un
 * clic sur une ville déjà visitée y emmène le joueur.
 */
export class WorldMapPanel extends Modal {
  private readonly view = $('.world-map-view', this.root);
  private readonly here = $('.world-map-here', this.root);
  private readonly load: () => Promise<WorldMapView>;
  private readonly onFly: (mapId: number) => void;
  /** Image de la carte, décodée une fois par version (son adresse change à chaque enregistrement). */
  private picture: { url: string; image: Promise<HTMLImageElement> } | null = null;
  /** Dernier dessin, réutilisé tant que l'image et les lieux explorés n'ont pas changé. */
  private drawn: { key: string; canvas: Promise<HTMLCanvasElement> } | null = null;

  constructor(load: () => Promise<WorldMapView>, onFly: (mapId: number) => void) {
    super('#world-map');
    this.load = load;
    this.onFly = onFly;
    $('#btn-world-map').addEventListener('click', () => this.toggle());
  }

  protected override reveal(): Promise<void> {
    return this.show();
  }

  /** Prépare la carte en arrière-plan (connexion, changement de carte) : l'ouvrir ensuite est immédiat. */
  async preload(): Promise<void> {
    const map = await this.load().catch(() => null);
    if (!map?.image) return;
    await idle();
    await this.drawing(map).catch(() => undefined);
  }

  async show(): Promise<void> {
    this.open();
    const current = this.startLoad();
    this.here.textContent = '';
    this.view.replaceChildren(el('p', { className: 'muted', textContent: 'Chargement…' }));
    const map = await this.load().catch(() => null);
    if (!current()) return;
    if (!map) {
      this.view.replaceChildren(el('p', { className: 'muted', textContent: 'Carte indisponible.' }));
      return;
    }
    this.here.textContent = map.here ? `Vous êtes ici : ${map.here}` : '';
    if (!map.image) {
      this.view.replaceChildren(el('p', { className: 'muted', textContent: 'La carte du monde n\'a pas encore été dessinée.' }));
      return;
    }
    const drawing = await this.drawing(map).catch(() => null);
    if (!current()) return;
    if (!drawing) {
      this.view.replaceChildren(el('p', { className: 'muted', textContent: 'Carte indisponible.' }));
      return;
    }
    const frame = el('div', { className: 'world-map-frame' }, drawing, this.overlay(map));
    const viewport = el('div', { className: 'world-map-viewport' }, frame);
    viewport.style.aspectRatio = `${map.width} / ${map.height}`;
    // Toute la carte tient à l'écran, qu'elle soit large ou haute.
    viewport.style.width = `min(100%, calc(70vh * ${map.width} / ${map.height}))`;
    const dragged = this.zoomable(viewport, frame, map);
    const areaAt = (event: MouseEvent) => {
      const rect = frame.getBoundingClientRect();
      const x = Math.floor(((event.clientX - rect.left) / rect.width) * map.width);
      const y = Math.floor(((event.clientY - rect.top) / rect.height) * map.height);
      // Une ville passe avant la route qui la traverse.
      const found = map.areas.filter((entry) => inArea(entry, x, y));
      return found.find(isRectArea) ?? found[0];
    };
    // Au survol, le nom de la ville ou de la route pointée (et si l'on peut s'y envoler).
    frame.addEventListener('pointermove', (event) => {
      const area = areaAt(event);
      const flyable = area !== undefined && canFlyTo(map, area);
      frame.classList.toggle('can-fly', flyable);
      frame.title = !area ? '' : `${area.name}${area.visited ? '' : ' (inexploré)'}${flyable ? ' — cliquez pour y voler' : ''}`;
    });
    frame.addEventListener('click', async (event) => {
      if (dragged()) return;
      const area = areaAt(event);
      if (!area || !map.fly || !isFlyDestination(map, area)) return;
      // Vol impossible pour l'instant (dans un bâtiment…) : la fenêtre s'ouvre quand même, bouton grisé et raison.
      const { allowed, missing } = map.fly;
      const confirmed = await confirmDialog({
        title: `Voler vers ${area.name} ?`, icon: 'type-air',
        ...(allowed ? { confirmLabel: 'Voler' } : { message: missing, confirmLabel: 'Vol impossible', disabled: true }),
      });
      if (!confirmed || !this.isOpen) return;
      this.close();
      this.onFly(area.mapId);
    });
    if (map.position) {
      const player = this.pin('world-map-player', map.position.x, map.position.y, map, map.here);
      // Taille réglée dans l'administration, en pixels de l'image : le repère suit la taille de la carte à l'écran.
      if (map.playerSize !== null) Object.assign(player.style, { width: `${(map.playerSize / map.width) * 100}%`, height: 'auto', aspectRatio: '1', scale: '1' });
      frame.append(player);
    }
    const hint = !map.fly ? null : el('p', {
      className: 'muted world-map-fly',
      textContent: map.fly.allowed ? 'Vol : cliquez sur une ville déjà visitée pour vous y envoler.' : map.fly.missing,
    });
    this.view.replaceChildren(viewport, ...(hint ? [hint] : []));
  }

  /**
   * Zoom de la carte : boutons +/-, ouverture zoomée sur le joueur, et glissement pour se déplacer. La carte reste
   * calée sur les bords du cadre. Rend une fonction qui dit si le dernier appui était un glissement (pas un clic).
   */
  private zoomable(viewport: HTMLElement, frame: HTMLElement, map: WorldMapView): () => boolean {
    // Point visé au centre du cadre, en fraction de la carte.
    let center = map.position
      ? { x: (map.position.x + 0.5) / map.width, y: (map.position.y + 0.5) / map.height }
      : { x: 0.5, y: 0.5 };
    let zoom = map.position ? OPENING_ZOOM : 1;
    const zoomOut = el('button', { className: 'world-map-zoom-btn', type: 'button', textContent: '−', ariaLabel: 'Dézoomer' });
    const zoomIn = el('button', { className: 'world-map-zoom-btn', type: 'button', textContent: '+', ariaLabel: 'Zoomer' });
    const apply = () => {
      // Le centre ne peut pas montrer au-delà des bords de la carte.
      const half = 0.5 / zoom;
      center = { x: Math.min(1 - half, Math.max(half, center.x)), y: Math.min(1 - half, Math.max(half, center.y)) };
      frame.style.transform = `translate(${(0.5 - center.x * zoom) * 100}%, ${(0.5 - center.y * zoom) * 100}%) scale(${zoom})`;
      frame.style.setProperty('--zoom', String(zoom));
      viewport.classList.toggle('zoomed', zoom > 1);
      zoomOut.disabled = zoom <= ZOOM_LEVELS[0]!;
      zoomIn.disabled = zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!;
    };
    const step = (direction: 1 | -1) => {
      const next = direction > 0 ? ZOOM_LEVELS.find((level) => level > zoom) : ZOOM_LEVELS.findLast((level) => level < zoom);
      if (next === undefined) return;
      // En zoomant, on revient sur le joueur ; en dézoomant, on garde la vue où elle est.
      if (direction > 0 && map.position && zoom === 1) center = { x: (map.position.x + 0.5) / map.width, y: (map.position.y + 0.5) / map.height };
      zoom = next;
      apply();
    };
    zoomOut.addEventListener('click', () => step(-1));
    zoomIn.addEventListener('click', () => step(1));
    viewport.append(el('div', { className: 'world-map-zoom' }, zoomIn, zoomOut));

    let drag: { id: number; x: number; y: number; from: { x: number; y: number } } | null = null;
    let moved = false;
    frame.addEventListener('pointerdown', (event) => {
      if (zoom <= 1 || event.button !== 0) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, from: center };
      moved = false;
    });
    frame.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      if (!moved) frame.setPointerCapture(event.pointerId);
      moved = true;
      viewport.classList.add('dragging');
      const rect = viewport.getBoundingClientRect();
      center = { x: drag.from.x - dx / (rect.width * zoom), y: drag.from.y - dy / (rect.height * zoom) };
      apply();
    });
    const release = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.id) return;
      drag = null;
      viewport.classList.remove('dragging');
    };
    frame.addEventListener('pointerup', release);
    frame.addEventListener('pointercancel', release);
    apply();
    return () => {
      const was = moved;
      moved = false;
      return was;
    };
  }

  /** Le dessin, avec les lieux jamais explorés passés en gris ; gardé tant que rien n'a changé. */
  private drawing(map: WorldMapView): Promise<HTMLCanvasElement> {
    const key = `${map.image} ${map.areas.map((area) => `${area.mapId}${area.visited ? '+' : '-'}`).join()}`;
    if (this.drawn?.key !== key) {
      const canvas = this.paint(map);
      this.drawn = { key, canvas };
      // Échec (réseau…) : on réessaiera à la prochaine ouverture.
      canvas.catch(() => { if (this.drawn?.canvas === canvas) this.drawn = null; });
    }
    return this.drawn.canvas;
  }

  private image(url: string): Promise<HTMLImageElement> {
    if (this.picture?.url !== url) {
      const image = new Image();
      image.src = url;
      const decoded = image.decode().then(() => image);
      this.picture = { url, image: decoded };
      decoded.catch(() => { if (this.picture?.image === decoded) this.picture = null; });
    }
    return this.picture.image;
  }

  private async paint(map: WorldMapView): Promise<HTMLCanvasElement> {
    const image = await this.image(map.image ?? '');
    const canvas = el('canvas', { className: 'world-map-drawing', width: map.width, height: map.height });
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Carte du monde');
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    ctx.drawImage(image, 0, 0);
    const unexplored = map.areas.filter((area) => !area.visited);
    if (!unexplored.length) return canvas;
    const pixels = ctx.getImageData(0, 0, map.width, map.height);
    const { data } = pixels;
    // Un lieu visité reste en couleurs même si une zone inexplorée le chevauche.
    const explored = new Set<number>();
    for (const area of map.areas) if (area.visited) forEachPixel(area, (x, y) => explored.add(y * map.width + x));
    const grey = (x: number, y: number) => {
      if (explored.has(y * map.width + x)) return;
      const i = (y * map.width + x) * 4;
      const luma = 0.3 * (data[i] ?? 0) + 0.59 * (data[i + 1] ?? 0) + 0.11 * (data[i + 2] ?? 0);
      // Gris clair mêlé d'un peu de la couleur d'origine, pour rester discret.
      const shade = 50 + luma * 0.6;
      for (let c = i; c < i + 3; c++) data[c] = Math.round(shade * 0.65 + (data[c] ?? 0) * 0.35);
    };
    for (const area of unexplored) forEachPixel(area, grey);
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }

  /**
   * Par-dessus le dessin : les lieux explorés en rouge, celui où se trouve le joueur en jaune. Dessiné plus fin que
   * la carte (OVERLAY_SCALE), pour arrondir légèrement les coins des villes.
   */
  private overlay(map: WorldMapView): HTMLCanvasElement {
    const scale = Math.max(1, Math.min(OVERLAY_SCALE, Math.floor(OVERLAY_MAX_SIDE / Math.max(map.width, map.height))));
    const canvas = el('canvas', { className: 'world-map-areas', width: map.width * scale, height: map.height * scale });
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    const current = map.position?.mapId;
    const paint = (area: WorldMapArea, color: string) => {
      ctx.fillStyle = color;
      if (isRectArea(area)) {
        ctx.beginPath();
        if (area.kind === 'place') ctx.ellipse((area.x + area.width / 2) * scale, (area.y + area.height / 2) * scale, area.width * scale / 2, area.height * scale / 2, 0, 0, Math.PI * 2);
        else ctx.roundRect(area.x * scale, area.y * scale, area.width * scale, area.height * scale, TOWN_CORNER * scale);
        ctx.fill();
      } else forEachPixel(area, (x, y) => ctx.fillRect(x * scale, y * scale, scale, scale));
    };
    for (const area of map.areas) if (area.visited && area.mapId !== current) paint(area, 'rgb(230 40 60 / .2)');
    const here = map.areas.find((area) => area.mapId === current);
    if (here) paint(here, 'rgb(255 214 64 / .2)');
    return canvas;
  }

  private pin(className: string, x: number, y: number, map: WorldMapView, title: string): HTMLElement {
    const pin = el('span', { className, title });
    pin.style.left = `${(x / map.width) * 100}%`;
    pin.style.top = `${(y / map.height) * 100}%`;
    return pin;
  }
}
